import { useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ChatMessage from './ChatMessage';
import MessageInput from './MessageInput';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useDispatch, useSelector } from 'react-redux';
import { clearSelectedConversation } from '../../redux/slices/conversationSlice';

const ChatWindow = ({ onNewMessage }) => {
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const disclaimerText =
    'This chat feature is designed to help students collaborate on MIS and support each other in a positive environment. Please communicate respectfully and use appropriate language at all times. All conversations should follow community guidelines and uphold the values of kindness, respect, and academic integrity.';
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    messagesByConversation,
    selectedConversationId,
    selectedConversation,
  } = useSelector((state) => state.conversation);
  const messages = messagesByConversation[selectedConversationId] || [];

  const handleSendMessage = async (message) => {
    try {
      console.log('Sending message:', message);
      onNewMessage(message);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
      // messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        border: isMobile ? 'none' : '1px solid #D3D3D3',
        margin: isMobile ? '0px' : '15px',
        borderRadius: isMobile ? '0px' : '12px',
        boxShadow: '0px 4px 16px 0px #0000001A',
        height: isMobile ? '100%' : 'calc(100% - 28px)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 20px',
          borderBottom: '1px solid #A6A6A6',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {/* Mobile back button to return to sidebar */}
          {isMobile && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mr: 0.5,
                cursor: 'pointer',
              }}
              onClick={() => dispatch(clearSelectedConversation())}
            >
              <ArrowBackIosNewIcon />
            </Box>
          )}

          <Avatar
            src={selectedConversation.avatar}
            sx={{ marginRight: '10px' }}
          />
          <Typography variant='h6'>{selectedConversation.name}</Typography>
        </Box>
        {/* <IconButton
          sx={{
            border: "2px solid black",
            padding: "0px",
            fontSize: "2rem",
          }}
        >
          <MoreVertIcon />
        </IconButton> */}
      </Box>
      <Box
        sx={{
          flex: 1,
          padding: '10px',
          overflowY: 'auto',
          overflowY: 'auto',
          // Improved scrollbar styling
          '&::-webkit-scrollbar': {
            width: '3px',
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: '3px',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.4)',
            },
          },
          // Better scroll performance
          scrollBehavior: 'smooth',
          '-webkit-overflow-scrolling': 'touch',
        }}
        ref={messagesContainerRef}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              width: '50%',
              borderRadius: '10px',
              border: '1px solid #D3D3D3',
              backgroundColor: '#F5F5F5',
              padding: '10px',
              marginBottom: '20px',
              textAlign: 'center',
            }}
          >
            {disclaimerText}
          </Typography>
        </Box>

        {messages &&
          messages.length > 0 &&
          messages.map(
            (msg, index) =>
              msg.text && (
                <ChatMessage
                  key={index}
                  message={msg.text}
                  time={msg.time || msg.date}
                  isCurrentUser={msg.isCurrentUser}
                  conversationUser={selectedConversation}
                  sender={msg.sender}
                />
              ),
          )}
        <div ref={messagesEndRef} />
      </Box>
      <Box
        sx={{
          position: 'sticky',
          bottom: { xs: '80px', sm: 0 },
        }}
      >
        <MessageInput onSendMessage={handleSendMessage} />
      </Box>
    </Box>
  );
};

export default ChatWindow;
